import React from 'react';

import { formatMoney, toMoney } from '@betterment/core';
import { useFlowState } from '@betterment/js-runtime';
import { Banner, Box, ContentLayoutF, FormikField, H2, Input, ListRow, Text, Tooltip } from '@betterment/samba-react';
import { FormikValidateAfterSubmit } from '@betterment/shared-components';
import { Form } from 'formik';
import * as Yup from 'yup';

import { BillingFrequency } from '../../../../graphql/schema';
import { parseFloatFromNumberInputMask } from '../../../../utils/input-helpers';
import FlowStepLayout from '../../../BettermentForAdvisors/FlowStepLayout';
import { makeUpsertBillingPlanInput } from '../api';
import { MAX_FIXED_FEE_AMOUNT } from '../constants';
import { BillingPlanFlowValues, PlatformFeePaidBy } from '../data';
import FeeSummaryBox from '../fee-summary-box/FeeSummaryBox';
import { useSubmitHandler } from '../hooks/useSubmitHandler';
import { PlatformFeeBox, platformFeeNettingSchema } from '../platform-fee-box/PlatformFeeBox';
import { getFlowTitle } from '../utils';

const Schema = Yup.object().shape({
  fixedFeeAmount: Yup.number()
    .transform((_, originalValue: string): number => {
      const parsedValue = parseFloatFromNumberInputMask(originalValue);
      // @ts-expect-error TS2322 violates strict null checks
      return Number.isNaN(parsedValue) ? null : parsedValue;
    })
    .required('Advisory fee is required')
    .min(0, '$0.00 minimum')
    .max(MAX_FIXED_FEE_AMOUNT, `${formatMoney(toMoney(MAX_FIXED_FEE_AMOUNT * 100), 'defaultDollar')} maximum`),
});

export const FixedBillingConfigurationStep: React.FC = () => {
  const {
    flowState: { values: flowValues },
  } = useFlowState<BillingPlanFlowValues>();
  const { submitHandler, errorMessage } = useSubmitHandler();
  const platformFeeTiers = flowValues.feeConfiguration?.platformFeeTiers || [];
  const validationSchema = Schema.concat(platformFeeNettingSchema(flowValues.platformFee));
  const billingFrequency = flowValues.billingFrequency.toLowerCase();
  const billingFrequencyNoun = flowValues.billingFrequency === BillingFrequency.Monthly ? 'month' : 'quarter';

  return (
    <FlowStepLayout exitToPath={flowValues.exitToPath} title={getFlowTitle(flowValues, 2)}>
      <Box
        css={{
          width: 'container.max',
          height: 'container.large',
          display: 'flex',
          flexDirection: 'column',
          justifySelf: 'center',
        }}
      >
        <Box css={{ pb: 'space.400' }}>
          <H2>Set fixed billing plan.</H2>
          {errorMessage && (
            <Banner variant="critical" css={{ mt: 'space.400' }}>
              <Banner.Content>{errorMessage}</Banner.Content>
            </Banner>
          )}
        </Box>
        <FormikValidateAfterSubmit
          onSubmit={async (values: {
            fixedFeeAmount: string;
            platformFeePaidBy: PlatformFeePaidBy;
            sharedPlatformFeeClientPortionBps?: string;
          }) => {
            await submitHandler({
              ...flowValues,
              fixedFeeAmount: values.fixedFeeAmount,
              platformFeePaidBy: values.platformFeePaidBy,
              sharedPlatformFeeClientPortionBps: values.sharedPlatformFeeClientPortionBps,
            });
          }}
          initialValues={{
            fixedFeeAmount: flowValues.fixedFeeAmount,
            platformFeePaidBy: flowValues.platformFeePaidBy,
            sharedPlatformFeeClientPortionBps: flowValues.sharedPlatformFeeClientPortionBps,
          }}
          validationSchema={validationSchema}
        >
          {({ values }) => (
            <Form>
              <ContentLayoutF>
                <ContentLayoutF.SlotContainer>
                  <ContentLayoutF.Slot1>
                    <Box
                      css={{
                        borderColor: 'border',
                        borderStyle: 'solid',
                        borderWidth: 'thin',
                        borderRadius: 'large',
                        marginBottom: 'space.300',
                        padding: 'space.300',
                      }}
                    >
                      <ListRow css={{ padding: 'space.0' }}>
                        <ListRow.LeadingContent css={{ gap: 'space.50' }}>
                          <H2 id="fixedFeeAmount" variant="title200" css={{ display: 'inline-flex' }}>
                            Annual advisory fee
                            <Tooltip>
                              <Tooltip.Trigger asChild={true} data-testid="advisoryFeeTooltip">
                                <Tooltip.IconButton name="InfoCircle" css={{ ml: 'space.50' }} />
                              </Tooltip.Trigger>
                              <Tooltip.Content align="center" side="top">
                                <Text>
                                  This is the annual rate that will apply to all of the accounts in a given household.
                                  It is collected on a {billingFrequency} basis in an amount reflecting the time that
                                  the household was funded during the {billingFrequencyNoun}.
                                </Text>
                              </Tooltip.Content>
                            </Tooltip>
                          </H2>
                          <Text color="text.weak" variant="text50">
                            Billed {billingFrequency}. Frequency can be edited in settings under fees.
                          </Text>
                        </ListRow.LeadingContent>
                        <ListRow.TrailingContent css={{ marginLeft: 'space.600' }}>
                          <FormikField name="fixedFeeAmount">
                            <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                              <FormikField.Control>
                                <Input
                                  name="fixedFeeAmount"
                                  aria-labelledby="fixedFeeAmount"
                                  alignment="left"
                                  appearance="default"
                                  leadingSymbol="$"
                                  mask="number"
                                  css={{
                                    width: '160px',
                                  }}
                                />
                              </FormikField.Control>
                              <FormikField.ErrorMessage />
                            </Box>
                          </FormikField>
                        </ListRow.TrailingContent>
                      </ListRow>
                    </Box>
                    <PlatformFeeBox platformFee={flowValues.platformFee} tiers={platformFeeTiers} />
                  </ContentLayoutF.Slot1>
                  <ContentLayoutF.Slot2>
                    <FeeSummaryBox
                      billingPlan={makeUpsertBillingPlanInput({
                        ...flowValues,
                        fixedFeeAmount: values.fixedFeeAmount,
                      })}
                      platformFee={flowValues.platformFee}
                      numberOfClients={flowValues.numberOfClients}
                    />
                  </ContentLayoutF.Slot2>
                </ContentLayoutF.SlotContainer>
              </ContentLayoutF>
            </Form>
          )}
        </FormikValidateAfterSubmit>
      </Box>
    </FlowStepLayout>
  );
};
